import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EditLeaveReasonInfoDialog, EditLeaveReasonInfoForm, LeaveReasonInfoPost } from '@core/models/leave-reasons/edit-leave-reason-info-dialog.model';
import { Observable, Subject } from 'rxjs';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { capitalizeFirst, indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { LeaveReasonDetails } from '@core/models/leave-reasons/leave-reason-details.model';
import { LeaveReasonsService } from '@core/services/leave-reasons.service';
import { LeaveReasonDetailsStoreService } from '@core/services/leave-reason-deatils-store.service';
import { ErrorService, LayoutService } from '@core/services';
import { takeUntil } from 'rxjs/operators';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { StiiraError } from '@core/models';
import { ProceedConfirmationComponent, ProceedConfirmationComponentData } from '../proceed-confirmation/proceed-confirmation.component';
import { OwnerOptions } from '@core/enums/owner-options.enum';

@Component({
    selector: 'app-edit-leave-reason-info',
    templateUrl: './edit-leave-reason-info.component.html',
    styleUrl: './edit-leave-reason-info.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditLeaveReasonInfoComponent {
  static readonly componentName = 'EditLeaveReasonInfoComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();
  
  public form: FormGroup<EditLeaveReasonInfoForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public isMobile$: Observable<boolean>;
  public position = position;
  public onDragEnded = onDragEnded;

  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { 
      leaveReasonDetails: LeaveReasonDetails;
      options: EditLeaveReasonInfoDialog;
      sysText: any; 
      unsavedChangesSysText: any
    },
    private dialogRef: MatDialogRef<EditLeaveReasonInfoComponent>,
    private service: LeaveReasonsService,
    private dialog: MatDialog,
    private fb: FormBuilder,  
    private store: LeaveReasonDetailsStoreService,
    private errorService: ErrorService,
    private layoutService: LayoutService
    ) { 
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group({
      leaveReasonName: this.fb.control(null, Validators.required),
      description: this.fb.control(null),
      ownerEmployerId: this.fb.control(null),
      allowUse: this.fb.control(null),
      allowUseByAllEmployers: this.fb.control(null),
      allowEmployerAssignments: this.fb.control(null)
    })
  }

  ngOnInit(): void {
    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.cancel());

    if (!this.data.leaveReasonDetails.ownerEmployerId) {
      this.data.options.employers = [ { id: OwnerOptions.Stock, description: "-- " + this.data.sysText.stock + " --" } ];
    }
    this.isMobile$ = this.layoutService.isMobile$();
    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setFormErrors() {
    setTimeout(() => {
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  public save(overrideWarning: boolean): void {
    const dto: LeaveReasonInfoPost = {
      overrideWarning: overrideWarning,
      leaveReasonId: this.data.leaveReasonDetails.id,
      leaveReasonName: this.form.controls.leaveReasonName.value,
      description: this.form.controls.description.value,
      ownerEmployerId: this.form.controls.ownerEmployerId.value,
      allowUse: this.form.controls.allowUse.value,
      allowUseByAllEmployers: this.form.controls.allowUseByAllEmployers.value,
      allowEmployerAssignments: this.form.controls.allowEmployerAssignments.value
    }
      this.service.postLeaveReasonInfo(dto)
        .pipe(indicate(this.isSubmitting$))
        .subscribe((res) => {
          if (res.showLeaveCaseWarning || res.showEmployerWarning) {
            this.openWarningDialog(res.showLeaveCaseWarning, res.showEmployerWarning);
          } else {
            this.store.leaveReasonDetails = res as LeaveReasonDetails;
            this.dialogRef.close();
          }
        }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
  }

  public cancel(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private setupForm(): void {
    this.form.patchValue({
      leaveReasonName: this.data.leaveReasonDetails.name,
      description: this.data.leaveReasonDetails.description,
      ownerEmployerId: this.data.leaveReasonDetails.ownerEmployerId ?? OwnerOptions.Stock,
      allowUse: this.data.leaveReasonDetails.allowUse,
      allowUseByAllEmployers: this.data.leaveReasonDetails.allowUseByAllEmployers,
      allowEmployerAssignments: this.data.leaveReasonDetails.allowEmployerAssignments
    });

    if (!this.data.options.canEditNameAndDescription) {
      this.form.controls.leaveReasonName.disable();
      this.form.controls.description.disable();
    }

    if (!this.data.options.canEditOwner) {
      this.form.controls.ownerEmployerId.disable();
    }

    if (!this.data.options.canEditSharingOptions) {
      this.form.controls.allowUseByAllEmployers.disable();
      this.form.controls.allowEmployerAssignments.disable();
    }

    if (!this.data.options.canEditAllowUse) {
      this.form.controls.allowUse.disable();
    }

    this.formInitValues = { ...this.form.value };
    this.setFormErrors();

    this.form.controls.allowUseByAllEmployers.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newValue) => {
        if (newValue)
            this.form.controls.allowEmployerAssignments.setValue(false);
        });

    this.form.controls.allowEmployerAssignments.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newValue) => {
        if (newValue)
          this.form.controls.allowUseByAllEmployers.setValue(false);
        });
  }

  private openWarningDialog(showLeaveCaseWarning: boolean, showEmployerWarning: boolean ): void {
    this.dialog.open<
      ProceedConfirmationComponent,
      ProceedConfirmationComponentData>
      (ProceedConfirmationComponent, {
        id: ProceedConfirmationComponent.componentName,
        panelClass: "mat-dialog-container-mobileWidth",
        width: '450px',
        disableClose: false,
        closeOnNavigation: true,
        data: {
          title: this.data.sysText.leaveReasonWarnTitle,
          message1: showLeaveCaseWarning ? this.data.sysText.leaveReasonLeaveCaseWarning : null,
          message2: showEmployerWarning ? 
            (showLeaveCaseWarning ? (this.data.sysText.inAddition + ' ' + this.data.sysText.leaveReasonEmployerWarning) : 
            capitalizeFirst(this.data.sysText.leaveReasonEmployerWarning)) : 
            null,
          cancelCaption: this.data.sysText.cancel,
          proceedCaption: this.data.sysText.proceed
        }
      }).beforeClosed().subscribe((res: boolean) => {
        if (res) {
          this.save(true);
        }
      });
  }
}
